import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Later } from "@/components/Later";
import { Failure, NotFound } from "@/features/shell/Problem";
import { OrderKindChoice } from "@/features/warehouseOrder/OrderKindChoice";
import { WarehouseOrderEntry } from "@/features/warehouseOrder/WarehouseOrderEntry";
import { ApiError } from "@/lib/api";
import { addressesQuery, orderFormQuery } from "@/lib/orderEntry";
import { warehouseOptionsQuery, type WarehouseKind } from "@/lib/warehouseOrder";

// Uitleveren uit ons magazijn, Aanleveren in ons magazijn (H-w01 … H-w03, docs/partner-portal.md §16 step 8): only for
// a customer with Voorraad in het portaal; ?kind= says which. Alleen transport is the order form (/order).

interface WarehouseOrderSearch {
  kind: WarehouseKind;
}

export const Route = createFileRoute("/_portal/warehouse-order")({
  validateSearch: (search: Record<string, unknown>): WarehouseOrderSearch => ({ kind: search.kind === "inbound" ? "inbound" : "outbound" }),
  component: WarehouseOrderScreen,
});

function WarehouseOrderScreen() {
  const { t } = useTranslation();
  const { kind } = Route.useSearch();
  const options = useQuery(warehouseOptionsQuery);
  const settings = useQuery(orderFormQuery);
  const book = useQuery(addressesQuery);

  if (options.error instanceof ApiError && options.error.status === 404) return <NotFound />;
  const failed = options.error ?? settings.error ?? book.error;
  if (failed) return <Failure error={failed} onRetry={() => void Promise.all([options.refetch(), settings.refetch(), book.refetch()])} />;

  return (
    <div className="wl-portal-page" data-screen-label={kind === "outbound" ? "Portaal Uitleveren" : "Portaal Aanleveren"}>
      <h1 className="t-h2">{t("nav.order")}</h1>
      <OrderKindChoice current={kind} />
      {!options.data || !settings.data || !book.data ? (
        <Later>
          <p className="t-body-sm" style={{ color: "var(--text-secondary)" }}>
            {t("warehouseOrder.loading")}
          </p>
        </Later>
      ) : options.data.warehouses.length === 0 ? (
        <div className="wl-empty">
          <div className="wl-empty__title">{t("warehouseOrder.noWarehouse")}</div>
          <p className="wl-empty__text">{t("warehouseOrder.noWarehouseText")}</p>
        </div>
      ) : (
        <WarehouseOrderEntry key={kind} kind={kind} options={options.data} settings={settings.data} book={book.data} />
      )}
    </div>
  );
}
